import { Button, Modal, Typography } from "antd";
import { useState } from "react";
import { AiOutlineCheckCircle, AiOutlineSetting } from "react-icons/ai";
import { useNavigate } from "react-router";

import { useCustomNotification } from "design-system/hooks";
import { pathsSchema } from "shell/platform/config/paths";

import s from "./ConfigureModal.module.css";

interface Option<T> {
  description?: string;
  id: T;
  name: string;
}

interface Props<T> {
  isOpen: boolean;
  onChange: (value: T) => void;
  onClose: () => void;
  options: Option<T>[];
  title: string;
  type: string;
  value: T;
}

export const ConfigureModal = <T extends string | number>({
  isOpen,
  onClose,
  options,
  value,
  onChange,
  title,
  type,
}: Props<T>) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<T>(value);
  const [oldValue] = useState<T>(value);
  const { showNotification } = useCustomNotification();

  const handleClose = () => {
    onClose();
    if (!isSaveDisabled) window.location.reload();
  };

  const handleSave = () => {
    onChange(selected);

    showNotification({
      type: "success",
      message: "Success",
      description: `Your ${type} is selected.`,
    });

    onClose();
    navigate(pathsSchema.portal.index);
  };

  const handleChange = (value: T) => setSelected(value);

  const isSaveDisabled = oldValue === selected;

  return (
    <Modal
      title={
        <header className={s.header}>
          <AiOutlineSetting size={25} /> <span>{title}</span>
        </header>
      }
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={700}
    >
      <section className={s.container}>
        <p>Select a default {type}.</p>
        <div className={s.options}>
          <div className={s.cards}>
            {options.map(data => (
              <section className={s.card} key={data.id}>
                <p>{type}</p>
                <Typography.Text strong>{data.name}</Typography.Text>
                {data.description && (
                  <span className={s.description}>{data.description}</span>
                )}
                <section>
                  {data.id === selected ? (
                    <p className={s.activeTag}>
                      <AiOutlineCheckCircle className={s.circleCheckIcon} />
                      Active {type}
                    </p>
                  ) : (
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => handleChange(data.id)}
                      className={s.setDefaultBtn}
                      data-testid={`option-card-${data.name}`}
                    >
                      Configure as default {type}
                    </Button>
                  )}
                </section>
              </section>
            ))}
          </div>
        </div>
      </section>
      <section className={s.actions}>
        <Button
          size="small"
          type="primary"
          onClick={handleSave}
          disabled={isSaveDisabled}
          className={s.saveBtn}
          data-testid="saveButton"
        >
          Save Changes
        </Button>
      </section>
    </Modal>
  );
};
